import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, Loader2, Trash2, Users, AlertTriangle,
  Shield, Landmark, Activity, FileText, CheckSquare, Calendar,
  MessageSquare, History, Tags, Save, GitMerge, ExternalLink, Calculator, Home, X,
} from 'lucide-react';
import ContactOpportunities from './ContactOpportunities';
import ContactLoans from './ContactLoans';
import ContactWorksheets from './ContactWorksheets';
import ContactRelationships from './ContactRelationships';
import ContactCommunications from './ContactCommunications';
import ContactActivity from './ContactActivity';
import ContactAudit from './ContactAudit';
import ContactTasks from './ContactTasks';
import ContactAppointments from './ContactAppointments';
import ContactAddressCard, { cleanAddress } from './ContactAddressCard';
import ContactQualification from './qualification/ContactQualification';
import UploadRequestsCard from './UploadRequestsCard';
import AudreySummaryButton from './AudreySummaryButton';
import AskAudreyButton from './AskAudreyButton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { formatShortDate } from './kit/time';
import PhoneInput from './shared/PhoneInput';
import { formatPhone } from './shared/phone';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { uc, CONTACT_TYPES, LEAD_STATUSES } from './unifiedContactsApi';
import { useTabParam } from './shared/useTabParam';
import { useAuth } from './AuthContext';
import { useOwnerMode } from './OwnerModeContext';
import { commsApi } from './phase567Api';
import { blockText, isDnd } from './sendStatus';
import { OwnerChip, useStaff } from './kit/OwnerChip';
import { errorMessage } from './errors';

const TABS = [
  { k: 'overview', l: 'Overview', icon: UsersIconWrap },
  { k: 'opportunities', l: 'Opportunities', icon: Landmark },
  { k: 'loans', l: 'Loans', icon: FileText },
  { k: 'worksheets', l: 'Worksheets', icon: Home },
  { k: 'relationships', l: 'Relationships', icon: Users },
  { k: 'qualification', l: 'Qualification', icon: Calculator },
  { k: 'communications', l: 'Communications', icon: MessageSquare },
  { k: 'activities', l: 'Activities', icon: Activity },
  { k: 'tasks', l: 'Tasks', icon: CheckSquare },
  { k: 'appointments', l: 'Appointments', icon: Calendar },
  { k: 'documents', l: 'Documents', icon: FileText },
  { k: 'marketing', l: 'Marketing', icon: Tags },
  { k: 'audit', l: 'Audit History', icon: History },
];

function UsersIconWrap(props) { return <Users {...props} />; }

const consentColor = (s) => s === 'granted' ? 'text-green-600 bg-green-50' :
  s === 'revoked' ? 'text-red-600 bg-red-50' : 'text-slate-500 bg-slate-50';
const consentLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown');

// Ticket 188 D5: the newest score wins; the server names its source and date.
const creditChipText = (c) => {
  if (!c.credit) return `FICO ${c.credit_score}`;
  const when = formatShortDate(c.credit.as_of);
  return ['FICO ' + c.credit.score, c.credit.source, when].filter(Boolean).join(' \u00b7 ');
};

export default function ContactProfilePage() {
  const { contactId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const canMerge = user?.is_super_admin || (user?.permissions || []).includes('contacts.merge');
  const perms = user?.permissions || [];
  // Mirrors backend ACTION_KEYS["contacts.delete"] -> contacts.merge.
  const canDelete = user?.is_super_admin || perms.includes('contacts.merge');
  const canSeeDocs = user?.is_super_admin || perms.includes('documents.view');
  const canIssueLinks = user?.is_super_admin || perms.includes('links.issue');
  // Fee worksheets are a mortgage feature (same gate as the Fee Worksheet nav entry).
  const { hasProduct } = useOwnerMode();
  const visibleTabs = TABS.filter((t) => (
    (t.k !== 'qualification' || perms.includes('qualification.run'))
    && (t.k !== 'worksheets' || hasProduct('mortgage'))
  ));
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useTabParam('overview', { valid: visibleTabs.map((t) => t.k) });
  const [dupes, setDupes] = useState([]);
  const [dupeOpen, setDupeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({});
  const staff = useStaff();
  // Ticket 141: live GHL DND + consent per channel. Null until loaded or on
  // error — the pills then fall back to consent alone; sends are gated
  // server-side either way.
  // statusFor drops a slow answer that lands after the page moved to
  // another contact (global search navigates without a remount).
  const [sendStatus, setSendStatus] = useState(null);
  const statusFor = useRef(contactId);
  const loadSendStatus = useCallback(() => {
    statusFor.current = contactId;
    commsApi.sendStatus(contactId)
      .then((st) => { if (statusFor.current === contactId) setSendStatus(st); })
      .catch(() => { if (statusFor.current === contactId) setSendStatus(null); });
  }, [contactId]);

  const canReassign = perms.includes('contacts.assign');
  const reassignOwner = async (ownerId) => {
    try {
      const updated = await uc.update(contactId, { owner_id: ownerId });
      setContact(updated);
      toast({ title: 'Owner changed' });
    } catch (e) {
      toast({ title: 'Owner change failed', description: errorMessage(e) });
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([
        uc.get(contactId),
        uc.duplicates(contactId).catch(() => ({ items: [] })),
      ]);
      setContact(c);
      setDupes(d.items || []);
      setEdit({});
    } catch (e) {
      toast({ title: 'Failed to load contact', description: e?.response?.data?.detail || String(e) });
      if (e?.response?.status === 404) nav('/portal/contacts');
    } finally {
      setLoading(false);
    }
  }, [contactId, nav]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSendStatus(null); }, [contactId]);
  useEffect(() => { loadSendStatus(); }, [loadSendStatus]);

  const save = async () => {
    if (Object.keys(edit).length === 0) return;
    setSaving(true);
    try {
      // "+ Add email/phone" appends an empty row; never persist it (ticket 98 N2).
      const patch = { ...edit };
      for (const k of ['emails', 'phones']) {
        if (Array.isArray(patch[k])) patch[k] = patch[k].filter((r) => (r?.value || '').trim());
      }
      // A tag write-through diffs against the list this page loaded, so a tag
      // added in GoHighLevel since then survives the save.
      if (patch.tags && contact.ghl_tags_editable) patch.tags_base = contact.tags || [];
      for (const k of ['mailing_address', 'current_property_address']) {
        if (k in patch) patch[k] = cleanAddress(patch[k]);
      }
      const updated = await uc.update(contactId, patch);
      setContact(updated);
      setEdit({});
      loadSendStatus();  // a new or removed address changes what may send
      toast({ title: 'Saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const recordConsent = async (channel, status) => {
    try {
      await uc.recordConsent(contactId, { channel, status, source: 'admin_override' });
      toast({ title: `${channel.toUpperCase()} consent ${status}` });
      loadSendStatus();
      // Awaited so the consent row stays locked until the new status lands.
      await load();
    } catch (e) {
      toast({ title: 'Consent failed', description: e?.response?.data?.detail || String(e) });
    }
  };

  const merge = async (dupId) => {
    if (!(await confirmDialog({ title: 'Merge this duplicate into the current contact?', description: 'This cannot be undone.', confirmText: 'Merge' }))) return;
    try {
      await uc.merge(contactId, dupId, 'survivor');
      toast({ title: 'Merged' });
      setDupeOpen(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast({ title: 'Merge failed', description: d?.code === 'ghl_duplicate' ? d.message : errorMessage(e) });
    }
  };

  const softDelete = async () => {
    if (!(await confirmDialog({ title: 'Delete this contact?', description: 'The contact is removed from your lists, and its saved qualification scenarios are deleted.', confirmText: 'Delete', destructive: true }))) return;
    try {
      await uc.remove(contactId);
      toast({ title: 'Contact deleted' });
      nav('/portal/contacts');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast({ title: 'Delete failed', description: d?.message || (typeof d === 'string' ? d : undefined) });
    }
  };

  if (loading) {
    return (
      <div className="p-20 flex items-center justify-center text-slate-400" data-testid="contact-loading">
        <Loader2 className="animate-spin mr-2" size={22} /> Loading contact...
      </div>
    );
  }
  if (!contact) return null;

  const v = (field) => edit[field] !== undefined ? edit[field] : contact[field];
  const setV = (field, value) => setEdit((e) => ({ ...e, [field]: value }));
  // Ticket 122: identity on a GHL mirror is edited in GoHighLevel, not here.
  const ghl = !!contact.ghl_managed;

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="contact-profile-page">
      {/* Top */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Link to="/portal/contacts" className="text-slate-400 hover:text-slate-700">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }}>
              {contact.display_name}
            </h1>
            <div className="text-sm text-slate-500 mt-0.5 flex gap-3 flex-wrap items-center">
              <span className="px-2 py-0.5 rounded-full bg-slate-100">
                {CONTACT_TYPES.find(t => t.k === contact.contact_type)?.l || contact.contact_type}
              </span>
              {contact.contact_type === 'lead' && contact.lead_status && (
                <span className="px-2 py-0.5 rounded-full bg-slate-100">
                  {LEAD_STATUSES.find(t => t.k === contact.lead_status)?.l || contact.lead_status}
                </span>
              )}
              {(contact.credit || contact.credit_score) && (
                <span className="px-2 py-0.5 rounded-full bg-slate-100" data-testid="contact-credit-score-chip">
                  {creditChipText(contact)}
                </span>
              )}
              <ChannelPill label="SMS" consent={contact.sms_consent} status={sendStatus} channel="sms" />
              <ChannelPill label="Email" consent={contact.email_consent} status={sendStatus} channel="email" />
              {isDnd(sendStatus?.call?.reason) && (
                <span className="px-2 py-0.5 rounded-full text-red-600 bg-red-50" data-testid="dnd-pill-call">
                  Calls: {blockText(sendStatus, 'call')}
                </span>
              )}
              {sendStatus?.inbound_dnd && (
                <span className="px-2 py-0.5 rounded-full text-red-600 bg-red-50" data-testid="dnd-pill-inbound">
                  Inbound DND in GoHighLevel
                </span>
              )}
              {['sms', 'email', 'call'].some((ch) => sendStatus?.[ch]?.reason === 'dnd_unchecked') && (
                <span className="px-2 py-0.5 rounded-full text-amber-700 bg-amber-50" data-testid="dnd-pill-unchecked">
                  Couldn't confirm DND with GoHighLevel
                </span>
              )}
              {ghl && (
                <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 inline-flex items-center gap-1.5" data-testid="ghl-managed-badge">
                  Managed in GoHighLevel
                  {contact.ghl_edit_url && (
                    <a href={contact.ghl_edit_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 underline hover:text-indigo-900" data-testid="ghl-edit-link">
                      Edit in GHL <ExternalLink size={11} />
                    </a>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AudreySummaryButton kind="contact" id={contactId} />
          <AskAudreyButton kind="contact" id={contactId} />
          {dupes.length > 0 && (
            <button
              onClick={() => setDupeOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-sm hover:bg-amber-100"
              data-testid="dupes-btn"
            >
              <AlertTriangle size={14} /> {dupes.length} possible duplicate{dupes.length > 1 ? 's' : ''}
            </button>
          )}
          {Object.keys(edit).length > 0 && (
            <button onClick={save} disabled={saving}
              className="btn-gold"
              data-testid="save-btn"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
            </button>
          )}
          {canDelete && !ghl && (
            <button onClick={softDelete}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-red-200 text-red-500 text-sm hover:bg-red-50"
              data-testid="delete-btn"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {visibleTabs.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition ${
                  tab === t.k
                    ? 'font-medium'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
                style={tab === t.k ? { borderColor: '#070B5F', color: '#070B5F' } : undefined}
                data-testid={`tab-${t.k}`}
              >
                <Icon size={14} /> {t.l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      {tab === 'overview' && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            {/* Basic */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="text-sm font-medium text-slate-500 mb-4">Basic Info</h3>
              <div className="grid grid-cols-2 gap-4">
                <Field label="First Name" value={v('first_name') || ''} onChange={(x) => setV('first_name', x)} testid="edit-first" disabled={ghl} />
                <Field label="Last Name" value={v('last_name') || ''} onChange={(x) => setV('last_name', x)} testid="edit-last" disabled={ghl} />
                <Field label="Preferred Name" value={v('preferred_name') || ''} onChange={(x) => setV('preferred_name', x)} />
                <SelectField label="Contact Type" value={v('contact_type')} options={CONTACT_TYPES} onChange={(x) => setV('contact_type', x)} />
                {v('contact_type') === 'lead' && (
                  <SelectField label="Lead Status" value={v('lead_status') || 'new'} options={LEAD_STATUSES} onChange={(x) => setV('lead_status', x)} />
                )}
                <Field label="Credit Score" value={v('credit_score') ?? ''} testid="edit-credit-score"
                  onChange={(x) => { const d = String(x).replace(/\D/g, '').slice(0, 3); setV('credit_score', d ? Number(d) : null); }} />
                <SelectField label="Communication Preference" value={v('communication_preference')}
                  options={[
                    { k: 'any', l: 'Any' }, { k: 'email', l: 'Email' }, { k: 'sms', l: 'SMS' },
                    { k: 'phone', l: 'Phone' }, { k: 'do_not_contact', l: 'Do Not Contact' },
                  ]} onChange={(x) => setV('communication_preference', x)} />
              </div>
            </div>

            {/* Contact channels */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="text-sm font-medium text-slate-500 mb-4">Emails & Phones</h3>
              <div className="space-y-2">
                {(v('emails') || []).map((e, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Mail size={14} className="text-slate-400" />
                    <Input value={e.value} disabled={ghl} onChange={(ev) => {
                      const arr = [...(v('emails') || [])]; arr[i] = { ...arr[i], value: ev.target.value };
                      setV('emails', arr);
                    }} />
                  </div>
                ))}
                {!ghl && <button className="text-xs text-slate-500 hover:text-slate-800" onClick={() => setV('emails', [...(v('emails') || []), { value: '' }])}>+ Add email</button>}
                {(v('phones') || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Phone size={14} className="text-slate-400" />
                    <PhoneInput value={p.value} disabled={ghl} onChange={(digits) => {
                      const arr = [...(v('phones') || [])]; arr[i] = { ...arr[i], value: digits };
                      setV('phones', arr);
                    }} />
                  </div>
                ))}
                {!ghl && <button className="text-xs text-slate-500 hover:text-slate-800" onClick={() => setV('phones', [...(v('phones') || []), { value: '' }])}>+ Add phone</button>}
              </div>
            </div>

            <ContactAddressCard
              mailing={v('mailing_address')}
              property={v('current_property_address')}
              onChange={setV}
              ghl={ghl}
            />

            <TagsCard tags={v('tags') || []} onChange={(x) => setV('tags', x)} ghl={ghl} writeThrough={!!contact.ghl_tags_editable} />

            {/* Notes */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <Label className="text-sm font-medium text-slate-500">Notes</Label>
              <Textarea value={v('notes') || ''} onChange={(e) => setV('notes', e.target.value)} rows={4} className="mt-2" data-testid="edit-notes" />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-500">
                <Shield size={14} /> Consent
              </div>
              <ConsentRow label="SMS" status={contact.sms_consent} dnd={dndText(sendStatus, 'sms')} onGrant={() => recordConsent('sms', 'granted')} onRevoke={() => recordConsent('sms', 'revoked')} />
              <ConsentRow label="Email" status={contact.email_consent} dnd={dndText(sendStatus, 'email')} onGrant={() => recordConsent('email', 'granted')} onRevoke={() => recordConsent('email', 'revoked')} />
              <p className="text-[11px] text-slate-400 mt-3">
                {(contact.consent_events || []).length} consent event(s) on file · append-only
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="text-sm font-medium text-slate-500 mb-3">Attribution</h3>
              <KV k="Referral Source" val={contact.referral_source || '—'} />
              <KV k="Original Lead Source" val={contact.original_lead_source || '—'} />
              <KV k="Assigned User" val={<OwnerChip userId={contact.owner_id || contact.assigned_user_id} staff={staff} showName testId="contact-owner"
                onChange={canReassign ? reassignOwner : undefined} />} />
              <KV k="Est. Lifetime Value" val={contact.estimated_lifetime_value ? `$${contact.estimated_lifetime_value.toLocaleString()}` : '—'} />
            </div>

            {canSeeDocs && <UploadRequestsCard key={contactId} contactId={contactId} canIssue={canIssueLinks} />}
          </div>
        </div>
      )}

      {tab === 'activities' && (
        <ContactActivity key={contactId} contactId={contactId} />
      )}
      {tab === 'audit' && (
        <ContactAudit key={contactId} contactId={contactId} />
      )}
      {tab === 'opportunities' && (
        <ContactOpportunities contactId={contactId} />
      )}
      {tab === 'loans' && (
        <ContactLoans contactId={contactId} />
      )}
      {tab === 'worksheets' && (
        <ContactWorksheets key={contactId} contactId={contactId} />
      )}
      {tab === 'relationships' && (
        <ContactRelationships contactId={contactId} />
      )}
      {tab === 'qualification' && (
        <ContactQualification contactId={contactId} loanId={searchParams.get('loan_id') || undefined} />
      )}
      {/* ghl_tags_editable is the write-through gate (GHL tenant, send on, linked
          contact) — the same one a new note on this contact goes through. */}
      {tab === 'communications' && (
        <ContactCommunications contactId={contactId} status={sendStatus} onStatusStale={loadSendStatus} notesToGhl={!!contact.ghl_tags_editable} />
      )}
      {tab === 'tasks' && (
        <ContactTasks contactId={contactId} ghlEditUrl={contact.ghl_edit_url} />
      )}
      {tab === 'appointments' && (
        <ContactAppointments
          contactId={contactId}
          contactName={contact.display_name}
          ghlManaged={!!contact.ghl_managed}
          ghlEditUrl={contact.ghl_edit_url}
        />
      )}
      {['documents', 'marketing'].includes(tab) && (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-16 text-center text-slate-400" data-testid={`tab-empty-${tab}`}>
          <p className="text-sm">This tab wires up in the next phases — data model is ready.</p>
          <p className="text-xs mt-1">Currently showing the timeline as a placeholder.</p>
        </div>
      )}

      {/* Duplicates modal */}
      <Dialog open={dupeOpen} onOpenChange={setDupeOpen}>
        <DialogContent className="max-w-2xl" data-testid="duplicates-modal">
          <DialogHeader><DialogTitle style={{ color: '#070B5F' }} className="font-serif">Possible Duplicates</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Matches on normalized email or phone. Merging keeps this contact as the survivor.</p>
          <ul className="divide-y divide-slate-100">
            {dupes.map(d => (
              <li key={d.contact_id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium">{d.display_name}</div>
                  <div className="text-xs text-slate-500">{[d.emails?.[0]?.value, formatPhone(d.phones?.[0]?.value)].filter(Boolean).join(' · ')}</div>
                </div>
                {canMerge && ghl && d.ghl_managed && (
                  <p className="text-xs text-slate-500 max-w-[16rem] text-right" data-testid={`ghl-pair-${d.contact_id}`}>
                    Both are managed in GoHighLevel. Merge them in GHL; BFY follows on the next sync.
                    {contact.ghl_edit_url && (
                      <a href={contact.ghl_edit_url} target="_blank" rel="noopener noreferrer"
                        className="ml-1 inline-flex items-center gap-0.5 underline" style={{ color: '#070B5F' }} data-testid={`ghl-pair-link-${d.contact_id}`}>
                        Open in GHL <ExternalLink size={11} />
                      </a>
                    )}
                  </p>
                )}
                {canMerge && !(ghl && d.ghl_managed) && (
                  <button
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-xs"
                    style={{ background: '#070B5F' }}
                    onClick={() => merge(d.contact_id)}
                    data-testid={`merge-btn-${d.contact_id}`}
                  >
                    <GitMerge size={12} /> Merge into this
                  </button>
                )}
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Little bits ----
// Tags are GHL-owned on a managed contact. With `writeThrough` (the API's
// ghl_tags_editable) a save sends the change to GoHighLevel first, which stores
// tags lower-case; without it the API 409s an edit, so they render read-only.
// A BFY-only contact edits them and saves with the page.
export function TagsCard({ tags, onChange, ghl, writeThrough = false }) {
  const [draft, setDraft] = useState('');
  const locked = ghl && !writeThrough;
  const add = () => {
    const t = ghl ? draft.trim().toLowerCase() : draft.trim();
    const have = ghl ? tags.some((x) => x.toLowerCase() === t) : tags.includes(t);
    if (t && !have) onChange([...tags, t]);
    setDraft('');
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="contact-tags-card">
      <h3 className="text-sm font-medium text-slate-500 mb-4 flex items-center gap-2">
        <Tags size={14} /> Tags{tags.length > 0 && ` (${tags.length})`}
      </h3>
      {tags.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="contact-tags-empty">
          {locked ? 'No tags. Add them in GoHighLevel.' : 'No tags yet.'}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((t) => (
            <span key={t} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs inline-flex items-center gap-1.5" data-testid="contact-tag">
              {t}
              {!locked && (
                <button type="button" className="p-1 -m-1 rounded-full text-slate-400 hover:text-slate-700" aria-label={`Remove tag ${t}`}
                  onClick={() => onChange(tags.filter((x) => x !== t))}>
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {!locked && (
        <Input value={draft} placeholder="Add a tag, then press Enter" className="mt-3" data-testid="contact-tag-input"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
      )}
      {ghl && writeThrough && (
        <p className="mt-2 text-xs text-slate-400" data-testid="contact-tags-ghl-hint">
          Saving updates this contact's tags in GoHighLevel.
        </p>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, disabled }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled} />
    </div>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o.k} value={o.k}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function KV({ k, val }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-xs text-slate-500">{k}</span>
      <span className="text-xs text-slate-800 truncate max-w-[60%] text-right">{val}</span>
    </div>
  );
}

// GHL DND copy for a channel, or null. DND outranks BFY consent: a
// "granted" contact on DND in GoHighLevel still can't be messaged.
const dndText = (status, channel) => (isDnd(status?.[channel]?.reason) ? blockText(status, channel) : null);

function ChannelPill({ label, consent, status, channel }) {
  const dnd = dndText(status, channel);
  if (dnd) {
    return (
      <span className="px-2 py-0.5 rounded-full text-red-600 bg-red-50" data-testid={`dnd-pill-${channel}`}>
        {label}: {dnd}
      </span>
    );
  }
  // Suppressed / unverified: never show a green "granted" for a channel
  // that can't send. (No address and revoked consent read fine as consent.)
  const blocked = ['suppressed', 'dnd_unchecked'].includes(status?.[channel]?.reason)
    ? blockText(status, channel) : null;
  return (
    <span className={`px-2 py-0.5 rounded-full ${blocked ? 'text-amber-700 bg-amber-50' : consentColor(consent)}`}
      data-testid={`consent-pill-${channel}`}>
      {label} consent: {consentLabel(consent)}{blocked && ` · ${blocked}`}
    </span>
  );
}

export function ConsentRow({ label, status, dnd, onGrant, onRevoke }) {
  const key = label.toLowerCase();
  const granted = status === 'granted';
  const known = status === 'granted' || status === 'revoked';
  // One consent change at a time: a double-click must not record two events.
  // Locked from the first click until the dialog closes and the save lands.
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const once = (fn) => async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try { await fn(); } finally { busyRef.current = false; setBusy(false); }
  };

  const revokeWithConfirm = once(async () => {
    const ok = await confirmDialog({
      title: `Revoke ${label} consent?`,
      description: 'Messages on this channel will be blocked until consent is granted again.',
      confirmText: 'Revoke',
    });
    if (ok) await onRevoke();
  });

  const grantWithConfirmIfRevoked = once(async () => {
    if (status !== 'revoked') { await onGrant(); return; }
    const ok = await confirmDialog({
      title: `Grant ${label} consent again?`,
      description: 'This contact revoked consent before. Only turn this on if they gave consent again.',
      confirmText: 'Grant',
    });
    if (ok) await onGrant();
  });

  const onCheckedChange = (checked) => {
    if (checked) grantWithConfirmIfRevoked();
    else revokeWithConfirm();
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
      <div className="text-sm text-slate-700">
        {label}
        {dnd && <div className="text-[10px] text-red-600" data-testid={`consent-dnd-${key}`}>{dnd}</div>}
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${consentColor(status)}`}
          data-testid={`consent-status-${key}`}>{consentLabel(status)}</span>
        {!known && (
          <Button variant="link" size="sm" className="h-auto p-0 text-[11px] text-slate-600 hover:text-slate-900"
            onClick={revokeWithConfirm} disabled={busy} data-testid={`consent-revoke-${key}`}
            aria-label={`Mark ${label} consent revoked`}>
            Mark revoked
          </Button>
        )}
        <Switch checked={granted} onCheckedChange={onCheckedChange} disabled={busy}
          aria-label={`${label} consent`} data-testid={`consent-toggle-${key}`} />
      </div>
    </div>
  );
}

export { TimelineList } from './ContactTimelineList';
